import React, { useState, useEffect } from 'react';
import { Button } from 'bench-internal-theme';
import { openModal } from 'modal-core-components';
import { Connection } from '../../types';
import DateRangePickerModal from './DateRangePickerModal';
import { toastError, toastSuccess } from 'bench-internal-theme';
import moment from 'moment';
import env from 'bench-environment';

type PreviewButtonProps = {
  connection: Connection | undefined | null;
};

const PreviewButton = ({ connection }: PreviewButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingConnection, setIsFetchingConnection] = useState(true);

  useEffect(() => {
    if (connection !== undefined) {
      setIsFetchingConnection(false);
    }
  }, [connection]);

  const previewTransactions = (startDate: string, endDate: string, closeModal: () => void) => {
    if (!connection) return toastError('Error', 'Error processing request.');

    const url = `${env.location.apiRoot}/financial-data/api/connection/${connection.id}/transactions/csv?from=${startDate}&to=${endDate}`;
    setIsLoading(true);
    return fetch(url, { credentials: 'include' })
      .then(() => {
        window.location.href = url;
        setIsLoading(false);
        closeModal();
        return toastSuccess('Connection', 'Transaction preview download starting.');
      })
      .catch(() => {
        setIsLoading(false);
        closeModal();
        return toastError(
          'Connection',
          'There was a problem previewing transactions for this connection.',
        );
      });
  };

  if (isFetchingConnection) {
    // Show loading state while connection is being fetched
    return (
      <>
        <Button disabled={true} styleType="primary">
          <span
            style={{
              display: 'inline-block',
              width: '16px',
              height: '16px',
              border: '2px solid transparent',
              borderTopColor: 'currentColor',
              borderRadius: '50%',
              marginRight: '8px',
              animation: 'spin 1s linear infinite',
            }}
          />
          Preview Transactions
        </Button>
        <style>
          {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
        </style>
      </>
    );
  }

  if (!connection) {
    // Handle undefined connection after API fails or returns no data
    return (
      <Button icon="close" disabled={true} styleType="primary">
        Error Loading
      </Button>
    );
  }

  const accounts = connection.accounts.map(account => account);
  const latestTransactions = accounts.map(account => account.latestTransactions.data).flat();
  const earliestTransaction = accounts.map(account => account.earliestTransactions.data).flat();

  const connectionHasTransactions = latestTransactions.length > 0;
  const maxDateAllowed =
    latestTransactions.length > 0
      ? moment.max(latestTransactions.map(transaction => moment(transaction.date)))
      : undefined;
  const minDateAllowed =
    earliestTransaction.length > 0
      ? moment.min(earliestTransaction.map(transaction => moment(transaction.date)))
      : undefined;

  const onClick = () =>
    openModal(closeModal => (
      <DateRangePickerModal
        defaultEndDate={maxDateAllowed}
        defaultStartDate={minDateAllowed}
        closeModal={closeModal}
        helperText={
          minDateAllowed &&
          maxDateAllowed &&
          `Data available between ${minDateAllowed.format(
            'MM/DD/YYYY',
          )} and ${maxDateAllowed.format('MM/DD/YYYY')}.`
        }
        description={`Preview transactions from the bank feed to assist with account mapping. Do not use this file for reconciliation as it may contain unvalidated data.`}
        onConfirm={(startDate, endDate) => previewTransactions(startDate, endDate, closeModal)}
        title="Preview Range"
      />
    ));

  return (
    <Button
      icon="download"
      disabled={isLoading || !connectionHasTransactions}
      styleType="primary"
      onClick={onClick}
    >
      Preview Transactions
    </Button>
  );
};

export default PreviewButton;
